import { uniqueId } from 'lodash'
import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import { secToHourMinute, secToMinute } from '../../../../utils/date'
import Tips from '../../common/Tips'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

interface Values {
  current: number | null
  prev: number | null
  team: number | null
  company: number | null
  occupation: number | null
  qasee: number | null
  checked: boolean
  appropriate_from: number | null
  appropriate_to: number | null
}

interface Props {
  target: string
  title: string
  display_prefix?: string
  display_title: string
  type: string
  values: Values
  labels: number[]
  half?: boolean
  upper_limit?: boolean
  lower_limit?: boolean
  appropriate?: number
  appropriate_position?: number
  button?: boolean
  help_text?: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void
  displaySearchLink: boolean
  color?: string
}

const VIEW_STANDARD_LIST = ["qasee_use_time", "qasee_access_count", "report_coverage_rate", "report_digging_count"]

const Deviation: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    target,
    title,
    display_prefix,
    display_title,
    type,
    values,
    labels,
    half,
    upper_limit,
    lower_limit,
    appropriate,
    appropriate_position,
    button,
    help_text,
    heatmapModalOpenCallback,
    displaySearchLink,
    color,
  } = props

  const [dataValues, setDataValues] = useState<Values>()

  const calcPosition = () => {
    const first = labels[0]
    const last = labels[labels.length - 1]
    const sum = last - first
    let current_value = dataValues?.current || 0
    if ((dataValues?.current || 0) > last) {
      current_value = last
    } else if ((dataValues?.current || 0) < first) {
      current_value = first
    }
    if (half) {
      return (((current_value - first) / sum) * 50) + 50
    }
    return ((current_value - first) / sum) * 100
  }

  const buildValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return (
        <span>
          -
        </span>
      )
    }
    if (type === "hour") {
      return (
        <>
          <span>
            {secToHourMinute((value || 0) * 3600)}
          </span>
          時間：分
        </>
      )
    }
    if (type === "minute") {
      return (
        <>
          <span>
            {secToHourMinute((value || 0) * 60)}
          </span>
          時間：分
        </>
      )
    }
    if (title === "work_time_variations" || title === "biz_point_variations") {
      return (
        <>
          <span>
            {(value || 0) >= 0 && "+"}
            {value.toLocaleString()}
          </span>
          {t(`report.detail.unit.${type}`)}
        </>
      )
    }
    return (
      <>
        <span>
          {value.toLocaleString()}
        </span>
        {t(`report.detail.unit.${type}`)}
      </>
    )
  }

  const calcAppropriatePosition = (score: number) => {
    const first = labels[0]
    const last = labels[labels.length - 1]
    const sum = last - first
    return (score / sum) * 100
  }

  useEffect(() => {
    if (type === "hour") {
      setDataValues(
        {
          current: values.current !== null ? (values.current / 3600) : null,
          prev: values.prev !== null ? (values.prev / 3600) : null,
          team: values.team !== null ? (values.team / 3600) : null,
          company: values.company !== null ? (values.company / 3600) : null,
          occupation: values.occupation !== null ? (values.occupation / 3600) : null,
          qasee: values.qasee !== null ? (values.qasee / 3600) : null,
          checked: values.checked,
          appropriate_from: values.appropriate_from !== null ? (values.appropriate_from / 3600) : null,
          appropriate_to: values.appropriate_to !== null ? (values.appropriate_to / 3600) : null,
        },
      )
    } else if (type === "minute") {
      setDataValues(
        {
          current: values.current !== null ? Number(secToMinute(values.current, false)) : null,
          prev: values.prev !== null ? Number(secToMinute(values.prev, false)) : null,
          team: values.team !== null ? Number(secToMinute(values.team, false)) : null,
          company: values.company !== null ? Number(secToMinute(values.company, false)) : null,
          occupation: values.occupation !== null ? Number(secToMinute(values.occupation, false)) : null,
          qasee: values.qasee !== null ? Number(secToMinute(values.qasee, false)) : null,
          checked: values.checked,
          appropriate_from: values.appropriate_from !== null ? Number(secToMinute(values.appropriate_from, false)) : null,
          appropriate_to: values.appropriate_to !== null ? Number(secToMinute(values.appropriate_to, false)) : null,
        },
      )
    } else {
      setDataValues(
        {
          current: values.current !== null ? values.current : null,
          prev: values.prev !== null ? values.prev : null,
          team: values.team !== null ? values.team : null,
          company: values.company !== null ? values.company : null,
          occupation: values.occupation !== null ? values.occupation : null,
          qasee: values.qasee !== null ? values.qasee : null,
          checked: values.checked,
          appropriate_from: values.appropriate_from !== null ? values.appropriate_from : null,
          appropriate_to: values.appropriate_to !== null ? values.appropriate_to : null,
        },
      )
    }
  }, [values])

  return (
    <div className="indicator_item">
      {
        dataValues?.checked && (
          <ReactSVG
            src="/static/images/icon/icon-warn.svg"
            className="icon_warn"
          />
        )
      }
      <div className="left">
        <div className={`text_area ${dataValues?.checked && "checked"}`}>
          {
            help_text ? (
              <Tips
                description={help_text}
                className="title_area"
              >
                <div className="title_wrap">
                  {
                    display_prefix && (
                      <span className="prefix">
                        {display_prefix}
                      </span>
                    )
                  }
                  <span className="title_detail">
                    {display_title}
                  </span>
                </div>
              </Tips>
            ) : (
              <div className="title_area">
                <div className="title_wrap">
                  {
                    display_prefix && (
                      <span className="prefix">
                        {display_prefix}
                      </span>
                    )
                  }
                  <span className="title_detail">
                    {display_title}
                  </span>
                </div>
              </div>
            )
          }
        </div>
        <div className={`values ${target} ${dataValues?.checked && "checked"}`}>
          {
            target === "team" ? (
              <>
                <div className="value">
                  {buildValue(dataValues?.current)}
                </div>
                <div className="value">
                  {buildValue(dataValues?.prev)}
                </div>
                <div className="value">
                  {buildValue(dataValues?.company)}
                </div>
                {
                  VIEW_STANDARD_LIST.includes(title) ? (
                    <div className="value standard">
                      {
                        title === "qasee_use_time" && (
                          <>
                            <span>0:20</span>
                            時間：分
                          </>
                        )
                      }
                      {
                        title === "qasee_access_count" && (
                          <>
                            <span>20</span>
                            回
                          </>
                        )
                      }
                      {
                        title === "report_coverage_rate" && (
                          <>
                            <span>70</span>
                            %
                          </>
                        )
                      }
                      {
                        title === "report_digging_count" && (
                          <>
                            <span>8</span>
                            回
                          </>
                        )
                      }
                    </div>
                  ) : (
                    <div className="value">
                      {buildValue(dataValues?.qasee)}
                    </div>
                  )
                }
              </>
            ) : (
              <>
                <div className="value">
                  {buildValue(dataValues?.current)}
                </div>
                <div className="value">
                  {buildValue(dataValues?.prev)}
                </div>
                <div className="value">
                  {buildValue(dataValues?.team)}
                </div>
                <div className="value">
                  {buildValue(dataValues?.company)}
                </div>
                <div className="value">
                  {buildValue(dataValues?.occupation)}
                </div>
              </>
            )
          }
        </div>
        <div className="button_area">
          {
            button && (
              <button
                data-gtm-click={`${t(`report.indicator.${title}`)} - button`}
                type="button"
                onClick={
                  () => heatmapModalOpenCallback({
                    type: type,
                    range: "monthly",
                    target: target,
                    id: 1,
                    title: title,
                    legend: `${t(`report.indicator.${title}`)}(${t(`report.detail.unit.${type}`)})`,
                    startColor: "#ECF0F5",
                    endColor: "#008FFB",
                    minimum: 0,
                    displaySearchLink: displaySearchLink,
                    name: "",
                  })
                }
                disabled={dataValues?.current === null}
              >
                比較を見る
              </button>
            )
          }
        </div>
      </div>
      <div className="right">
        <div className="graph">
          {
            half ? (
              <>
                {
                  Array((labels.length * 2) - 1).fill(null).map((_label, index) => (
                    <div
                      key={`${title}_bar_${uniqueId()}`}
                      className="vertical"
                      style={{
                        left: `${(index / ((labels.length - 1) * 2)) * 100}%`,
                      }}
                    />
                  ))
                }
                <div className="horizontal half" />
                {
                  !dataValues?.appropriate_to ? (
                    <>
                      {
                        appropriate ? (
                          <>
                            <div
                              className="appropriate_horizontal"
                              style={{
                                left: `${(appropriate_position || 0) + 50}%`,
                                width: `${appropriate}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${(appropriate_position || 0) + 50}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${(appropriate_position || 0) + appropriate + 50}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_text"
                              style={{
                                left: `${(appropriate_position || 0) + 50}%`,
                                width: `${appropriate}%`,
                              }}
                            >
                              適正範囲
                            </div>
                          </>
                        ) : (
                          <>
                            <div
                              className="base_vertical"
                              style={{
                                backgroundColor: color,
                              }}
                            />
                            <div className="base_text">
                              基準値
                            </div>
                          </>
                        )
                      }
                    </>
                  ) : (
                    <>
                      {
                        dataValues?.appropriate_to && dataValues?.appropriate_from ? (
                          <>
                            <div
                              className="appropriate_horizontal"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                width:
                                `${calcAppropriatePosition(dataValues.appropriate_to) - calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_to)}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_text"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                width:
                                `${calcAppropriatePosition(dataValues.appropriate_to) - calcAppropriatePosition(dataValues.appropriate_from)}%`,
                              }}
                            >
                              適正範囲
                            </div>
                          </>
                        ) : (
                          <>
                            <div
                              className="base_vertical"
                              style={{
                                backgroundColor: color,
                              }}
                            />
                            <div className="base_text">
                              基準値
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </>
            ) : (
              <>
                {
                  labels.map((_label, index) => (
                    <div
                      key={`${title}_bar_${uniqueId()}`}
                      className="vertical"
                      style={{
                        left: `${(index / (labels.length - 1)) * 100}%`,
                      }}
                    />
                  ))
                }
                <div className="horizontal" />
                {
                  !dataValues?.appropriate_to ? (
                    <>
                      {
                        appropriate ? (
                          <>
                            <div
                              className="appropriate_horizontal"
                              style={{
                                left: `${(appropriate_position || 0) + 50}%`,
                                width: `${appropriate}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${(appropriate_position || 0) + 50}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${(appropriate_position || 0) + appropriate + 50}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_text"
                              style={{
                                left: `${(appropriate_position || 0) + 50}%`,
                                width: `${appropriate}%`,
                              }}
                            >
                              適正範囲
                            </div>
                          </>
                        ) : (
                          <>
                            <div
                              className="base_vertical"
                              style={{
                                backgroundColor: color,
                              }}
                            />
                            <div className="base_text">
                              基準値
                            </div>
                          </>
                        )
                      }
                    </>
                  ) : (
                    <>
                      {
                        dataValues?.appropriate_to && dataValues?.appropriate_from ? (
                          <>
                            <div
                              className="appropriate_horizontal"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                width:
                                `${calcAppropriatePosition(dataValues.appropriate_to) - calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_vertical"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_to)}%`,
                                backgroundColor: color,
                              }}
                            />
                            <div
                              className="appropriate_text"
                              style={{
                                left: `${calcAppropriatePosition(dataValues.appropriate_from)}%`,
                                width:
                                `${calcAppropriatePosition(dataValues.appropriate_to) - calcAppropriatePosition(dataValues.appropriate_from)}%`,
                              }}
                            >
                              適正範囲
                            </div>
                          </>
                        ) : (
                          <>
                            <div
                              className="base_vertical"
                              style={{
                                backgroundColor: color,
                              }}
                            />
                            <div className="base_text">
                              基準値
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </>
            )
          }
          {
            dataValues?.current === null || (
              <div
                className={`graph_point ${dataValues?.checked && "checked"}`}
                style={{
                  left: `${calcPosition()}%`,
                }}
              />
            )
          }
        </div>
        <div className="label">
          {
            half ? (
              <>
                {
                  labels.map((label, index) => (
                    <div
                      key={`${title}_label_${uniqueId()}`}
                      className="label_text"
                      style={{
                        left: `${((index / ((labels.length - 1) * 2)) * 100) + 50}%`,
                      }}
                    >
                      {
                        index === 0 && lower_limit && (
                          <>
                            ~
                          </>
                        )
                      }
                      {label}
                      {t(`report.detail.unit.${type}`)}
                      {
                        index === labels.length - 1 && upper_limit && (
                          <>
                            ~
                          </>
                        )
                      }
                    </div>
                  ))
                }
              </>
            ) : (
              <>
                {
                  labels.map((label, index) => (
                    <div
                      key={`${title}_label_${uniqueId()}`}
                      className="label_text"
                      style={{
                        left: `${(index / (labels.length - 1)) * 100}%`,
                      }}
                    >
                      {
                        index === 0 && lower_limit && (
                          <>
                            ~
                          </>
                        )
                      }
                      {label}
                      {t(`report.detail.unit.${type}`)}
                      {
                        index === labels.length - 1 && upper_limit && (
                          <>
                            ~
                          </>
                        )
                      }
                    </div>
                  ))
                }
              </>
            )
          }
        </div>
      </div>
    </div>
  )
}

Deviation.defaultProps = {
  display_prefix: undefined,
  half: undefined,
  upper_limit: undefined,
  lower_limit: undefined,
  appropriate: undefined,
  appropriate_position: 0,
  button: true,
  help_text: undefined,
  color: undefined,
}

export default Deviation
